import * as Tabs from "@radix-ui/react-tabs";
import classNames from "classnames";
import cx from "classnames";
import { ReactNode } from "react";
import { NavLink } from "react-router-dom";

const Suffix = ({ children }: { children: ReactNode }) => {
  return (
    <span className="t-rounded t-max-h-[1.32rem] t-flex t-items-center t-justify-center t-border t-border-solid t-px-1 t-py-0.5 t-text-center t-text-caption t-font-semibold group-data-[state=active]:t-border-purple group-data-[state=inactive]:t-border-neutral-10 group-data-[state=active]:t-bg-purple group-data-[state=active]:t-text-white">
      {children}
    </span>
  );
};

const BottomBorder = () => {
  return (
    <div className="t-h-1 t-w-full t-rounded-t-lg group-hover:t-bg-purple-20 group-data-[state=active]:!t-bg-purple group-[&]/active:!t-bg-purple [state=active]:t-block [state=inactive]:t-hidden t-absolute t-bottom-0 t-transform t-transition-colors t-ease-in-out" />
  );
};

export type Triggerprops = {
  customType?: "secondary" | "primary";
  children: ReactNode;
  suffix?: string | number;
} & Tabs.TabsTriggerProps &
  React.RefAttributes<HTMLButtonElement>;

export type Listprops = Tabs.TabsListProps &
  React.RefAttributes<HTMLDivElement>;

const TAB_TYPE_CLASSES = {
  /* @tw */
  PRIMARY:
    "t-text-text-30 t-text-body-sm hover:t-text-text-100 group-data-[state=active]:t-text-purple group-[&]/active:t-text-purple",
  /* @tw */
  SECONDARY:
    "t-rounded t-py-1.5 t-px-2 t-text-body-sm t-font-medium t-text-i-text-light data-[state=active]:t-bg-purple group-[&]/active:t-bg-purple group-data-[state=active]:t-text-text group-[&]/active:t-text-text group-data-[state=active]:t-shadow-active-tab group-[&]/active:t-shadow-active-tab",
};

const Trigger = ({
  children,
  suffix,
  customType = "primary",
  className,
  ...props
}: Triggerprops) => {
  return (
    <Tabs.Trigger
      {...props}
      className={cx(
        "all:unset t-group t-cursor-pointer t-relative t-flex t-flex-col t-items-center t-gap-2 t-whitespace-nowrap	",
        {
          [TAB_TYPE_CLASSES.PRIMARY]: customType === "primary",
          [TAB_TYPE_CLASSES.SECONDARY]: customType === "secondary",
        },
        className
      )}
    >
      <div>
        <div className="t-flex t-items-center t-gap-2 group-data-[state=active]:t-text-purple t-pb-2">
          {children}
          {suffix !== undefined && <Suffix>{suffix}</Suffix>}
        </div>
        {customType === "primary" && <BottomBorder />}
      </div>
    </Tabs.Trigger>
  );
};

const NavTrigger = ({
  children,
  exact,
  replace,
  omitUrl,
  target,
  suffix,
  to,
  useAutoSelect = true,
  isActiveFn,
  ...props
}: {
  children: ReactNode;
  omitUrl?: boolean;
  exact?: boolean;
  replace?: boolean;
  target?: string;
  to?: string;
  useAutoSelect?: boolean;
  isActiveFn?: (match: any, location: any) => boolean;
} & Triggerprops) => {
  const { value } = props;

  return (
    <Tabs.Trigger
      {...props}
      className="all:unset t-group sm:t-shrink-0 sm:t-whitespace-nowrap"
    >
      <NavLink
        exact={Boolean(exact)}
        isActive={isActiveFn}
        className={(isActive) => {
          return cx("all:unset t-group t-cursor-pointer t-relative", {
            [TAB_TYPE_CLASSES.PRIMARY]: true,
            "t-group/active !t-text-purple": useAutoSelect && isActive,
            "all:unset t-flex t-flex-col t-items-center t-gap-2": isActive,
          });
        }}
        to={to || value}
        replace={replace}
        target={target}
      >
        <div
          className={classNames("t-pb-2", {
            "t-flex t-items-center t-gap-2": suffix,
          })}
        >
          {children}
          {suffix !== undefined && <Suffix>{suffix}</Suffix>}
        </div>
        <BottomBorder />
      </NavLink>
    </Tabs.Trigger>
  );
};

const List = ({ className = "", ...props }: Listprops) => {
  return (
    <Tabs.List
      {...props}
      className={classNames(
        "t-flex t-overflow-x-auto	hide-scrollbar t-gap-8 t-pr-2 t-pt-2 t-mb-3 t-border-0 t-border-b t-border-solid t-border-neutral-0",
        {
          [className]: className,
        }
      )}
    />
  );
};

const Tab = {
  ...Tabs,
  Trigger: Trigger,
  NavTrigger: NavTrigger,
  List: List,
  RawTrigger: Tabs.Trigger,
  RawList: Tabs.List,
};

export default Tab;
